import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { FaFile, FaDownload, FaTimes, FaCalendarAlt, FaUser, FaClock } from 'react-icons/fa';
import { useAuth } from '../context/AuthContext';
import { motion } from 'framer-motion';
import '../styles/markdown.css';

function BlogDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);
  const { user } = useAuth();

  // İçeriği formatlamak için yardımcı fonksiyon
  const formatContent = (content) => {
    if (!content) return '';
    let formattedContent = content;
    formattedContent = formattedContent
      .replace(/^#{1,6}\s+(.+)$/gm, '$1')
      .replace(/^\s*[-*+]\s+(.+)$/gm, '• $1')
      .replace(/^\d+\.\s+(.+)$/gm, '$1')
      .replace(/\*\*(.+?)\*\*/g, '$1')
      .replace(/\*(.+?)\*/g, '$1')
      .replace(/`(.+?)`/g, '$1');
    return formattedContent;
  };

  useEffect(() => {
    const fetchBlog = async () => {
      if (!user) {
        navigate('/login', { state: { from: `/blog/${id}` } });
        return;
      }

      try {
        setLoading(true);
        const docRef = doc(db, 'blogs', id);
        const docSnap = await getDoc(docRef);
        
        if (docSnap.exists()) {
          const blogData = docSnap.data();
          setBlog({
            id: docSnap.id,
            ...blogData,
            summary: formatContent(blogData.summary),
            tags: blogData.tags || []
          });
        }
      } catch (err) {
        console.error('Blog yüklenirken hata:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchBlog();
  }, [id, user, navigate]);

  const handleDownload = (file) => {
    if (!user) {
      navigate('/login', { state: { from: `/blog/${id}` } });
      return;
    }
    
    try {
      if (file.data) {
        const link = document.createElement('a');
        link.href = file.data;
        link.download = file.name;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        throw new Error('Dosya verisi bulunamadı');
      }
    } catch (error) {
      console.error('Dosya indirilirken hata:', error);
      alert('Dosya indirilirken bir hata oluştu. Lütfen tekrar deneyin.');
    }
  };

  const handleImageClick = (imageUrl) => {
    setSelectedImage(imageUrl);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex justify-center items-center bg-gradient-to-br from-blue-50 to-indigo-50">
        <div className="relative">
          <div className="w-16 h-16 border-4 border-blue-200 border-t-blue-500 rounded-full animate-spin"></div>
          <div className="mt-4 text-blue-600 font-medium">Yükleniyor...</div>
        </div>
      </div>
    );
  }

  if (!blog) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50">
      {/* Hero Section */}
      <div className="relative bg-gradient-to-r from-blue-600 to-indigo-700 text-white py-20">
        <div className="absolute inset-0 opacity-10" style={{ 
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cpath d='M0 0h60v60H0z'/%3E%3Cpath d='M30 30h30v30H30z' fill='currentColor' opacity='.05'/%3E%3Cpath d='M0 0h30v30H0z' fill='currentColor' opacity='.05'/%3E%3C/g%3E%3C/svg%3E")`,
          backgroundRepeat: 'repeat'
        }}></div>
        <div className="container mx-auto px-4 relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl mx-auto text-center"
          >
            <h1 className="text-4xl md:text-5xl font-bold mb-6">{blog.title}</h1>
            
            <div className="flex flex-wrap justify-center items-center gap-6 text-blue-100">
              <div className="flex items-center">
                <FaUser className="mr-2" />
                <span>{blog.author}</span>
              </div>
              <div className="flex items-center">
                <FaCalendarAlt className="mr-2" />
                <span>{new Date(blog.createdAt?.toDate()).toLocaleDateString('tr-TR')}</span>
              </div>
              <div className="flex items-center">
                <FaClock className="mr-2" />
                <span>{new Date(blog.createdAt?.toDate()).toLocaleTimeString('tr-TR')}</span>
              </div>
            </div>
          </motion.div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-blue-50 via-blue-50/80 to-transparent"></div>
      </div>

      <div className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto">
          {/* Etiketler */}
          {Array.isArray(blog.tags) && blog.tags.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="mb-8 flex flex-wrap gap-2"
            >
              {blog.tags.map((tag, index) => (
                <span
                  key={index}
                  className="px-4 py-2 bg-blue-100 text-blue-700 rounded-xl text-sm font-medium hover:bg-blue-200 transition-colors duration-200"
                >
                  {tag}
                </span>
              ))}
            </motion.div>
          )}

          {/* İçerik */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="prose prose-lg max-w-none mb-12 bg-white rounded-2xl p-8 shadow-xl shadow-blue-100"
          >
            <div 
              dangerouslySetInnerHTML={{ __html: blog.summary }}
              className="markdown-content"
            />
          </motion.div>

          {/* Dosyalar */}
          {Array.isArray(blog.files) && blog.files.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="bg-white rounded-2xl p-8 shadow-xl shadow-blue-100"
            >
              <h3 className="text-2xl font-bold mb-6 text-gray-800">
                Ekler ({blog.files.length})
              </h3>
              <div className="grid gap-4">
                {blog.files.map((file, idx) => (
                  <div 
                    key={idx}
                    className="group relative bg-blue-50 rounded-xl p-6 hover:bg-blue-100 transition-all duration-300"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <div className="w-12 h-12 bg-blue-200 rounded-xl flex items-center justify-center">
                          <FaFile className="text-blue-600 text-xl" />
                        </div>
                        <div>
                          <h4 className="text-lg font-semibold text-gray-800 group-hover:text-blue-700 transition-colors">
                            {file.name}
                          </h4>
                          <p className="text-sm text-gray-500">
                            {(file.size / 1024 / 1024).toFixed(2)} MB
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => handleDownload(file)}
                        className="flex items-center space-x-2 px-6 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors group"
                      >
                        <FaDownload className="group-hover:animate-bounce" />
                        <span>İndir</span>
                      </button>
                    </div>
                    
                    {file.type?.startsWith('image/') && file.data && (
                      <div className="mt-6">
                        <img 
                          src={file.data} 
                          alt={file.name}
                          className="w-full rounded-xl shadow-lg hover:shadow-xl transition-shadow cursor-pointer"
                          style={{ maxHeight: '400px', objectFit: 'contain' }}
                          onClick={() => handleImageClick(file.data)}
                        />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </motion.div>
          )}
        </div>
      </div>

      {/* Resim Görüntüleyici Modal */}
      {selectedImage && (
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-90"
          onClick={() => setSelectedImage(null)}
        >
          <button
            onClick={() => setSelectedImage(null)}
            className="absolute top-6 right-6 text-white hover:text-gray-300 transition-colors"
          >
            <FaTimes size={24} />
          </button>
          <img
            src={selectedImage}
            alt="Büyük görüntü"
            className="max-w-full max-h-[90vh] object-contain rounded-2xl"
          />
        </motion.div>
      )}
    </div>
  );
}

export default BlogDetail; 